import React, { useMemo, useState } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  ListItemIcon,
  Paper,
  Link,
  Tooltip,
  Select,
  MenuItem,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import BreadcrumbNavbar from '../components/BreadcrumbNavbar';
import { StyledTab, StyledTabs } from '../components/StyledComponents';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { YellowBoxBottom } from './Home/Home';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useAuth } from '../context/AuthContext';

// Predefined questions for each category
const QUESTIONS = {
  Marriage: {
    Timing: [
      'What is the most favorable time for me to get married?',
      'Is marriage likely to happen for me this year?',
    ],
    Harmony: [
      'What will my home environment be like after marriage?',
      'What kind of home environment will I have after marriage?',
    ],
    Traits: [
      'What will my life partner’s behavior or nature be like?',
      'What type of partner would be most compatible with me?',
      'What will be the traits or characteristics of my life partner?',
      'What will my life partner’s appearance or looks be like?',
      'Will my husband be tall, dark, and handsome?',
    ],
    'Love or Arrange': [
      'Will my marriage be a love marriage or an arranged one?',
    ],
    Compatibility: ['Which zodiac signs are most compatible with me?'],
    Doshas: [
      'Do I have any doshas that could affect my marriage?',
      'Does Mangal Dosh affect my marriage prospects?',
    ],
    'Multiple Marriage': [
      'Does my birth chart indicate the possibility of multiple marriages?',
    ],
    Delay: ['Will there be any delay in my marriage?'],
    Others: [
      'How important is Kundali matching for marriage?',
      'Should we select an auspicious date for our wedding?',
    ],
  },
  Finance: [
    'When will I get financial stability',
    'Will I become wealthy in life',
    'Should I invest in stock market',
  ],
  Health: [
    'Do I have any health issues in my chart',
    'What precautions should I take',
    'How will my overall health be',
  ],
  Career: [
    'Which career path is suitable for me',
    'Will I get success in my current job',
    'Should I switch my career',
  ],
  'Foreign Education': [
    'Will I go abroad for studies',
    'Which country is best for me',
    'Will I settle abroad permanently',
  ],
};

const FAQS = [
  {
    q: 'What is KaliyugAi and how is it different from other astrology platforms?',
    a: 'KaliyugAi is an AI-powered platform that blends ancient Vedic astrology with modern technology. Unlike generic apps, we provide accurate, ethical, and personalized guidance without fear-based tactics or random predictions. Our AI is trained on verified astrological scriptures and real practitioner insights.',
  },
  {
    q: 'Is the guidance really given by AI or a human astrologer?',
    a: 'Our responses are generated by a custom-built AI model trained on authentic astrological knowledge, reviewed and constantly improved by experienced astrologers. No real human interference in chat.',
  },
  {
    q: 'How accurate is the information I get from KaliyugAi?',
    a: 'KaliyugAi currently delivers up to 80.2% accuracy in marriage-related predictions, based on extensive testing and historical astrological datasets.',
  },
  {
    q: 'Do I need to know my exact birth time?',
    a: 'Yes, accurate predictions need your date, time, and place of birth. If your time is approximate, we’ll still provide general insights but may suggest a BTR (Birth-Time Rectification) method for precision.',
  },
  {
    q: 'Can KaliyugAi answer love and marriage questions?',
    a: 'Absolutely! Our platform offers detailed insights on love life, partner compatibility, marriage timing, and future spouse characteristics all based on your birth chart.',
  },
  {
    q: 'Is my data safe on your platform?',
    a: 'Yes. We follow strict privacy protocols. Your birth details and chat history are encrypted and never shared with third parties.',
  },
  {
    q: 'Can I get gemstone recommendations through KaliyugAi?',
    a: 'Yes. Based on your planetary alignments and doshas, our system suggests suitable gemstones. We also offer access to authentic, certified gemstones through our trusted network.',
  },
  {
    q: 'Are your gemstones lab-tested and authentic?',
    a: 'Yes. We’re building a transparent gemstone ecosystem. All gemstones are lab-certified, and each purchase comes with documentation to prove its origin and purity.',
  },
  {
    q: 'Is this a subscription service? Are there hidden charges?',
    a: 'No hidden charges. We operate on a transparent, minimalistic pricing model. You pay only for what you use whether it’s a question or a gemstone.',
  },
  {
    q: 'Can I ask follow-up questions if I don’t understand the answer?',
    a: 'Yes. Our system allows clarification and follow-ups within the same session so that you walk away with real understanding, not just predictions. Some follow up questions may be chargeable.',
  },
  {
    q: 'Can I use KaliyugAi in my regional language?',
    a: 'We are currently available in English and expanding to more languages soon so that astrology becomes accessible to all.',
  },
  {
    q: 'Do you support remedies and rituals?',
    a: 'Yes. Based on your chart, we may suggest relevant Vedic remedies, including mantras, donations, fasts, and temple visits only when necessary and never exaggerated.',
  },
  {
    q: 'What if my problem is very personal or emotional?',
    a: 'Our system is designed to be empathetic and respectful. Your data and concerns remain confidential, and we never judge or record emotional concerns.',
  },
  {
    q: 'How does the token system work after payment?',
    a: 'Once your payment is successful, the equivalent token value is instantly credited to your account. You can see your current token balance displayed at the top-right corner of the chat screen.',
  },
  {
    q: 'How many tokens does one question cost?',
    a: 'Each question you ask deducts 10 tokens from your account. After making a payment, your token balance is instantly updated and can be seen at the top-right corner of your chat screen. This transparent system ensures that you always know how many questions you can ask, with no hidden fees or surprises.',
  },
  {
    q: 'How do I start using KaliyugAi?',
    a: 'Just create an account, input your birth details, and start exploring the wisdom of astrology through a clean, easy-to-use interface. You can also chat with KaliyugAi directly to ask questions!',
  },
];

export default function IdeaWhatToAsk() {
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState('Marriage');
  const { isLogin } = useAuth();
  const handleTabChange = (_, newValue) => {
    setSelectedTab(newValue);
  };

  const handleQuestionClick = (question) => {
    if (isLogin) {
      // Navigate with state or query param to prefill the chat
      navigate('/chat', { state: { prefill: question } });
    } else {
      navigate('/signin');
    }
  };

  const [filter, setFilter] = useState('All');

  const selectOptions = useMemo(
    () => ['All', ...Object.keys(QUESTIONS[selectedTab])],
    [selectedTab]
  );
  const questions = useMemo(() => {
    if (filter === 'All') {
      return Object.values(QUESTIONS[selectedTab]).reduce(
        (accumulator, currentArray) => {
          return accumulator.concat(currentArray);
        },
        []
      );
    } else {
      return QUESTIONS[selectedTab][filter];
    }
  }, [filter, selectedTab]);

  return (
    <>
      <BreadcrumbNavbar />
      <Box sx={{ p: 6 }}>
        {/* Title */}
        <Typography variant="h2">Pre Asked Questions</Typography>
        <Typography variant="h4" gutterBottom>
          Browse our Pre asked Questions
        </Typography>

        <Box
          display="flex"
          alignItems="center"
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
        >
          {/* Tabs */}
          <StyledTabs
            value={selectedTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ mt: 10, maxWidth: '75%' }}
          >
            {Object.keys(QUESTIONS).map((category) =>
              category === 'Marriage' ? (
                <StyledTab key={category} value={category} label={category} />
              ) : (
                <Tooltip
                  title="We’re working to bring this to you soon."
                  placement="top"
                  disableInteractive
                  enterTouchDelay={0}
                  leaveTouchDelay={3000}
                >
                  {/* <span> */}
                  <StyledTab
                    key={category}
                    value={category}
                    // label={category}
                    disabled
                    label={
                      <Typography
                        noWrap
                        sx={{
                          display: 'block',
                          width: '100%',
                          color: '#454545B3',
                        }}
                      >
                        {category}
                      </Typography>
                    }
                    sx={{
                      maxWidth: {
                        xs: '200px',
                        sm: '200px',
                        md: '200px',
                        lg: '200px',
                      }, // or whatever width limit you want
                      textTransform: 'none',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      '& .MuiTab-wrapper': {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        textAlign: 'left', // ✅ ensures text truncates from right side
                        width: '100%',
                      },
                    }}
                  />
                  {/* </span> */}
                </Tooltip>
              )
            )}
          </StyledTabs>

          {/* Dropdown */}
          <Select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            variant="standard"
            sx={{ ml: 'auto', minWidth: 50, mt: 10 }}
            disableUnderline
            MenuProps={{
              PaperProps: {
                sx: {
                  boxShadow: 'none', // ⬅️ removes shadow
                  border: '1px solid #BABABA', // optional: add subtle border
                },
              },
            }}
          >
            {selectOptions.map((option) => (
              <MenuItem value={option}>{option}</MenuItem>
            ))}
          </Select>
        </Box>

        {/* Question List */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: 4,
            mb: 10,
          }}
        >
          {questions.map((q, idx) => (
            <ListItemButton
              key={idx}
              onClick={() => handleQuestionClick(q)}
              sx={{
                borderBottom: '1px solid #e0e0e0',
              }}
            >
              <ListItemText primary={q} />
              <ListItemIcon sx={{ minWidth: 'auto' }}>
                <ChevronRightIcon fontSize="small" />
              </ListItemIcon>
            </ListItemButton>
          ))}
        </Box>

        <Typography variant="h2">FAQ's</Typography>
        <Typography variant="h4" gutterBottom>
          Questions asked frequently
        </Typography>

        {/* FAQ Grid */}
        <Box
          sx={{
            mt: 8,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            '& > div': {
              px: { md: 3 }, // spacing inside each column
            },
          }}
        >
          {/* Left Column */}
          <Box sx={{ borderRight: { md: '1px solid #ddd' } }}>
            {FAQS.slice(0, Math.ceil(FAQS.length / 2)).map((item, idx) => (
              <Box key={idx} sx={{ pb: 2, mb: 2, mr: { xs: 0, md: 10 } }}>
                <Typography
                  variant="body1"
                  fontWeight={600}
                  gutterBottom
                  sx={{ borderBottom: '1px solid #eee', mb: 1, pb: 1 }}
                >
                  {item.q}
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight={500}
                  color="textSecondary"
                >
                  {item.a}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Right Column */}
          <Box>
            {FAQS.slice(Math.ceil(FAQS.length / 2)).map((item, idx) => (
              <Box key={idx} sx={{ pb: 2, mb: 2, ml: { xs: 0, md: 10 } }}>
                <Typography
                  variant="body1"
                  gutterBottom
                  fontWeight={600}
                  sx={{ borderBottom: '1px solid #eee', mb: 1, pb: 1 }}
                >
                  {item.q}
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight={500}
                  color="textSecondary"
                >
                  {item.a}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            mt: 6,
            mb: 10,
          }}
        >
          <Paper
            sx={{
              border: '1px solid #e0e0e0',
              padding: 2,
              my: 2,
              boxShadow: 'none',
              width: { xs: '100%', md: '60%' },
            }}
          >
            <Typography variant="body1" textAlign={'center'} fontWeight={500}>
              Questions, feedback, or just curious? Write to us at we’d love to
              hear from you!
              <br />
              <br />
              Email:{' '}
              <Link href="mailto:support@kaliyugai.com" color="secondary">
                support@kaliyugai.com
              </Link>
            </Typography>
          </Paper>
          <YellowBoxBottom sx={{ mt: 4, width: { xs: '100%', md: '60%' } }}>
            <Typography
              variant="h2"
              fontWeight={600}
              gutterBottom
              my={4}
              sx={{ cursor: 'pointer' }}
              onClick={() =>
                navigate('/home/Community_&_Impact/We_are_Different')
              }
            >
              How we are Different{' '}
              <OpenInNewIcon sx={{ fontSize: 16, ml: 0.5 }} />
            </Typography>
            <Typography variant="body1" textAlign={'center'} px={4}>
              Backed by Vedic science and AI, we offer clear, ethical, and
              personalized guidance no fear tactics, just trusted insights for
              confident living.
            </Typography>
          </YellowBoxBottom>
        </Box>
      </Box>
    </>
  );
}
